import {
    InvoiceListItemDto,
    InvoiceMutationDto,
    LineItemGroupMutationDto,
    LineItemMutationDto,
    LineItemType,
    MetaDataDto,
} from '../../../config/finance/invoiceConfig'
import { DataPromise } from '../../../context/AxiosContext'
import { useCallback, useMemo } from 'react'
import { EntityDto } from '../../../config/appConfig'
import { createLineItemMap } from '../../../components/Form/Finance/Invoice/LineItemGroups'
import { DisbursementListItemDto, TimeListItemDto, TravelListItemDto } from '../../../config/registrationConfig'
import * as UUID from 'uuid'

import { PriceVat } from '../../../components/Form/Finance/Invoice/InvoiceForm'
import { formatTimeInMinutes, multiplyTime, parseNumber, parseTimeInMinutes } from '../../../helpers/numberHelper'
import { formatPriceVat, getPriceVat, reducePrices } from '../../../helpers/priceHelper'
import { PriceDto, PricePrecedence } from '../../../config/finance/financeConfig'
import useCurrency from '../../useCurrency'
import { VatType } from '../../../config/i18nConfig'

export type LineItemEntity = EntityDto & {
    date: string
}

export type LineItemEntities = {
    selection: Map<LineItemType, LineItemEntity[]> // All used line items by line item type.
    orphans: Map<LineItemType, LineItemEntity[]> // All unused line items by line item type.
    hasOrphans: boolean // Whether any unused line items still exist in this file.
    entityTypeMap: Map<string, LineItemType>
    entityMap: Map<string, LineItemEntity>
    map: (item: LineItemEntity) => LineItemMutationDto
    getPrice: (entity: LineItemEntity, vat: VatType) => PriceVat
    getGroupPrice: (group: LineItemGroupMutationDto, vat: VatType, recalculate?: boolean) => PriceVat
    getTotalTime: (entities: LineItemMutationDto[]) => string
    getTotalDistance: (entities: LineItemMutationDto[]) => string
}

export default function useLineItemEntities(
    meta: DataPromise<MetaDataDto>,
    values?: InvoiceMutationDto,
): LineItemEntities {
    const selectedIds = useMemo(
        () =>
            (values?.lineItemGroups || [])
                .flatMap(lineItemGroup => lineItemGroup.lineItems)
                .map(lineItem => lineItem.entityId),
        [values, values?.lineItemGroups],
    )
    const selection = useMemo<Map<LineItemType, LineItemEntity[]>>(
        () => createLineItemMap(selectedIds, meta.data, true),
        [meta.data, selectedIds],
    )
    const orphans = useMemo<Map<LineItemType, LineItemEntity[]>>(
        () => createLineItemMap(selectedIds, meta.data, false),
        [meta.data, selectedIds],
    )
    const hasOrphans = useMemo<boolean>(() => [...orphans.values()].some(entities => entities.length > 0), [orphans])
    const [entityTypeMap, entityMap] = useMemo<[Map<string, LineItemType>, Map<string, LineItemEntity>]>(() => {
        const entities = [...createLineItemMap([], meta.data, false).entries()]
        return [
            new Map(entities.flatMap(([lineItemType, entities]) => entities.map(entity => [entity.id, lineItemType]))),
            new Map(entities.flatMap(([, entities]) => entities.map(entity => [entity.id, entity]))),
        ]
    }, [orphans])
    const { currency } = useCurrency(values?.currency)

    const map = useCallback(
        (item: LineItemEntity): LineItemMutationDto => ({
            id: UUID.v4(),
            type: entityTypeMap.get(item.id),
            entityId: item.id,
        }),
        [entityTypeMap],
    )

    const getPrice = useCallback(
        (item: LineItemEntity, vat: VatType): PriceVat => {
            if (!item) {
                return { subtotal: null, vat: [], total: null }
            }
            switch (entityTypeMap.get(item.id)) {
                case LineItemType.Time:
                    return formatPriceVat(
                        multiplyTime(
                            (item as TimeListItemDto)?.duration,
                            parseNumber((item as TimeListItemDto)?.rate?.amount || 0, 'en'),
                        ),
                        vat,
                        currency,
                    )
                case LineItemType.Travel:
                    return formatPriceVat(
                        parseNumber((item as TravelListItemDto)?.distance, 'en') *
                            parseNumber((item as TravelListItemDto)?.rate?.amount || 0, 'en'),
                        vat,
                        currency,
                    )
                case LineItemType.Disbursement:
                    return formatPriceVat(
                        parseNumber((item as DisbursementListItemDto)?.rate?.amount || 0, 'en'),
                        vat,
                        currency,
                    )
                case LineItemType.Invoice:
                    const invoicePrice: PriceDto = (item as InvoiceListItemDto)?.price
                    if (!invoicePrice) {
                        return { subtotal: 0, vat: [], total: 0 }
                    }

                    return {
                        subtotal: parseNumber(invoicePrice.subtotal, 'en') * -1,
                        vat: (invoicePrice.vat || []).map(vat => ({
                            value: parseNumber(vat.value, 'en') * -1,
                            type: vat.type,
                        })),
                        total: parseNumber(invoicePrice.total, 'en') * -1,
                    }
                default:
                    return { subtotal: null, vat: [], total: null }
            }
        },
        [entityTypeMap],
    )

    const getGroupPrice = useCallback(
        (group: LineItemGroupMutationDto, vat: VatType, recalculate?: boolean): PriceVat => {
            if (group.precedence === PricePrecedence.Inherited || recalculate) {
                return getPriceVat(
                    group.lineItems
                        .map(lineItem => getPrice(entityMap.get(lineItem.entityId), vat))
                        .reduce(reducePrices, {
                            subtotal: 0,
                            vat: [],
                            total: 0,
                        }).subtotal,
                    vat,
                    currency,
                )
            }

            return getPriceVat(parseNumber(group.price, 'en'), vat, currency, group.precedence)
        },
        [entityMap, getPrice],
    )

    const getTotalTime = useCallback(
        (entities: LineItemMutationDto[]): string => {
            return formatTimeInMinutes(
                entities
                    ?.filter(entity => entity.type === LineItemType.Time)
                    ?.map(entity => entityMap.get(entity.entityId))
                    ?.map(entity => (entity as TimeListItemDto)?.duration)
                    ?.map(duration => parseTimeInMinutes(duration))
                    ?.reduce((a, b) => a + b, 0),
            )
        },
        [entityMap],
    )

    const getTotalDistance = useCallback(
        (entities: LineItemMutationDto[]): string => {
            return entities
                ?.filter(entity => entity.type === LineItemType.Travel)
                ?.map(entity => entityMap.get(entity.entityId))
                ?.map(entity => (entity as TravelListItemDto)?.distance)
                ?.map(distance => parseInt(distance))
                ?.reduce((a, b) => a + b, 0)
                .toString()
        },
        [entityMap],
    )

    return {
        selection,
        orphans,
        hasOrphans,
        entityMap,
        entityTypeMap,
        map,
        getPrice,
        getGroupPrice,
        getTotalTime,
        getTotalDistance,
    }
}
